/** How lengths of audio are written for people. Pure, no dependencies, so any module (and a server) can use them. */

/** Formats a duration in seconds as "m:ss", extracted from
 * AudioTrimmer.tsx -- genuinely reusable wherever a duration needs
 * showing to a person rather than a raw seconds count. */
export function formatMinSec(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** "m:ss" or "m:ss.t" when the time has a fractional part (so 3.5 s is not shown as 0:03). */
export function formatMinSecTenth(totalSeconds: number): string {
  const t = Math.round(totalSeconds * 10) / 10;
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  const whole = Number.isInteger(s);
  return `${m}:${(whole ? String(s) : s.toFixed(1)).padStart(whole ? 2 : 4, "0")}`;
}

/**
 * Length of a clip as people read it: tenths of a second under a minute ("0:00.9", "0:02.7", "0:04.0"), so a
 * 4 s clip at 1.5x reads 0:04.0 -> 0:02.7 and not 0:04 -> 0:02, and a 0.919 s clip is not "0:00". From a minute up
 * it is plain m:ss. Rounds to the tenth first, so 59.97 s reads 1:00, never 0:60.0.
 */
export function formatDuration(totalSeconds: number): string {
  const t = Math.max(0, Math.round(totalSeconds * 10) / 10);
  if (t >= 60) return formatMinSec(Math.round(totalSeconds));
  return `0:${t.toFixed(1).padStart(4, "0")}`;
}
